import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { Play, RefreshCw } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useProcessPlan } from "@/features/planning/api";
import { useReadyProductionStages } from "@/features/production/api";
import { ExecuteStageSheet } from "@/features/production/execute-stage-sheet";
import type { ProcessPlanStage } from "@/features/planning/types";
import { isSupabaseConfigured } from "@/lib/supabase";

export function ProductionPage() {
  return (
    <ErrorBoundary title="Production crashed">
      <ProductionInner />
    </ErrorBoundary>
  );
}

function ProductionInner() {
  const { data = [], isLoading, error, refetch, isFetching } = useReadyProductionStages();
  const [executingPlanId, setExecutingPlanId] = useState<string | null>(null);
  const [executingStageId, setExecutingStageId] = useState<string | null>(null);
  const { data: plan } = useProcessPlan(executingPlanId ?? undefined);

  const stage = useMemo(() => {
    if (!plan || !executingStageId) return null;
    return (plan.stages ?? []).find((s) => s.id === executingStageId) ?? null;
  }, [plan, executingStageId]);

  if (!isSupabaseConfigured) {
    return (
      <EmptyState
        title="Supabase not configured"
        description="Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to use production."
      />
    );
  }

  return (
    <div className="space-y-3">
      <PageHeader
        title="Production"
        description="Execute stages · QC-required stages block next until verified · genealogy written on complete"
        actions={
          <Button type="button" variant="outline" size="sm" disabled={isFetching} onClick={() => refetch()}>
            <RefreshCw className={isFetching ? "animate-spin" : ""} />
            Refresh
          </Button>
        }
      />

      {error ? <p className="text-sm text-destructive">{(error as Error).message}</p> : null}

      {!isLoading && data.length === 0 ? (
        <EmptyState
          title="No stages ready"
          description="Mark a process plan as Planned, then run stages from here or the plan detail."
          action={
            <Button asChild size="sm">
              <Link to="/planning">Open planning</Link>
            </Button>
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Plan</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead>Process</TableHead>
              <TableHead>Machine</TableHead>
              <TableHead>Input</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow key={row.stage_id}>
                  <TableCell>
                    <Link
                      to="/planning/$planId"
                      params={{ planId: row.process_plan_id }}
                      className="font-mono text-xs font-medium text-primary hover:underline"
                    >
                      {row.plan_no}
                    </Link>
                    <div className="text-2xs text-muted-foreground">
                      {format(parseISO(row.plan_date), "dd MMM yyyy")}
                    </div>
                  </TableCell>
                  <TableCell className="tabular-nums">{row.stage_index}</TableCell>
                  <TableCell className="text-xs">{row.process_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {row.machine_code}
                    {row.machine_name ? ` · ${row.machine_name}` : ""}
                  </TableCell>
                  <TableCell className="text-2xs text-muted-foreground">
                    {row.input_source === "previous_stage" ? "Prev stage" : "Inventory"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        setExecutingPlanId(row.process_plan_id);
                        setExecutingStageId(row.stage_id);
                      }}
                    >
                      <Play />
                      Run
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <ExecuteStageSheet
        open={Boolean(executingStageId && stage)}
        onOpenChange={(o) => {
          if (!o) {
            setExecutingStageId(null);
            setExecutingPlanId(null);
          }
        }}
        stage={stage as ProcessPlanStage | null}
      />
    </div>
  );
}
